import React, { useState, useEffect } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Avatar,
  Button,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBars,
  faAnglesLeft,
  faSun,
  faMoon,
  faChevronDown,
  faRightFromBracket,
  faUser,
  faUserGear,
} from "@fortawesome/free-solid-svg-icons";
import FlexBetween from "./FlexBetween";
import NotificationBell from "./NotificationBell";
import TcmbRateTicker from "./TcmbRateTicker";
import { headerNavItems, routeActive } from "./sidebarNavConfig";
import { setMode, clearUser } from "../state";
import { getTheme } from "../theme/modernTheme";

const Navbar = ({ user, isSidebarOpen, setIsSidebarOpen }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  
  // Redux'tan tema modunu al
  const mode = useSelector((state) => state.global.mode);
  const currentTheme = getTheme(mode);
  const isOpen = Boolean(anchorEl);
  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const ink = currentTheme.colors.text.primary;
  const onInk = currentTheme.colors.text.button || "#0a0a0a";

  const handleLogout = () => {
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("userId");
      sessionStorage.removeItem("userId");
    } catch {}
    dispatch(clearUser());
    handleClose();
    navigate("/login");
  };

  const handleProfileSettings = () => {
    navigate("/profil");
    handleClose();
  };

  useEffect(() => {
    const readLocal = () => {
      try {
        const userInfo = localStorage.getItem("user");
        return userInfo ? JSON.parse(userInfo) : null;
      } catch {
        return null;
      }
    };

    const applyPayload = (payload) => {
      if (!payload) return;
      setCurrentUser({
        name: payload.name || "Kullanıcı",
        occupation:
          payload.occupation || payload.jobTitle || payload.role || "—",
        profileImage: payload.profileImage || null,
      });
    };

    applyPayload(readLocal() || { name: "Kullanıcı", occupation: "—", profileImage: null });

    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get("/api/general/user/current");
        if (cancelled) return;
        const payload = data?.user || data;
        applyPayload(payload);
      } catch {
        // axiosAuth interceptor will handle 401; otherwise keep localStorage values.
      }
    })();

    const handleStorageChange = (e) => {
      if (e.key === "user" && e.newValue) {
        try { applyPayload(JSON.parse(e.newValue)); } catch {}
      }
    };
    const handleUserUpdate = (e) => applyPayload(e.detail);

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("userUpdated", handleUserUpdate);

    return () => {
      cancelled = true;
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("userUpdated", handleUserUpdate);
    };
  }, []);

  const iconBtnSx = {
    color: currentTheme.colors.text.secondary,
    flexShrink: 0,
    "&:hover": {
      color: currentTheme.colors.primary,
      backgroundColor: currentTheme.colors.surface.secondary,
    },
  };

  return (
    <AppBar
      sx={{
        position: "static",
        flexShrink: 0,
        overflow: "visible",
        background: currentTheme.colors.surface.primary,
        backdropFilter: currentTheme.effects.backdrop,
        border: `1px solid ${currentTheme.colors.border.primary}`,
        boxShadow: currentTheme.shadows.sm,
        borderRadius: currentTheme.borderRadius.md,
        margin: "8px 12px",
        marginBottom: "4px",
      }}
    >
      <TcmbRateTicker />
      <Toolbar
        variant="dense"
        disableGutters
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          minHeight: 52,
          height: 52,
          maxHeight: 52,
          px: 1.5,
          gap: 1,
          overflow: "visible",
          flexWrap: "nowrap",
        }}
      >
        {/* LEFT SIDE */}
        <FlexBetween sx={{ gap: 0.75, minWidth: 0, flex: 1, mr: 1 }}>
          <Tooltip title={isSidebarOpen ? "Kenar çubuğunu gizle" : "Kenar çubuğunu göster"}>
            <IconButton
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              aria-label={isSidebarOpen ? "Kenar çubuğunu gizle" : "Kenar çubuğunu göster"}
              sx={{
                color: currentTheme.colors.text.secondary,
                flexShrink: 0,
                "&:hover": {
                  color: currentTheme.colors.primary,
                  backgroundColor: currentTheme.colors.surface.secondary,
                },
              }}
            >
              <FontAwesomeIcon
                icon={isSidebarOpen ? faAnglesLeft : faBars}
                style={{
                  fontSize: "1.15rem",
                  color: currentTheme.colors.text.secondary,
                }}
              />
            </IconButton>
          </Tooltip>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.35,
              minWidth: 0,
              overflowX: "auto",
              overflowY: "hidden",
              flex: 1,
              py: 0.25,
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {headerNavItems.map(({ label, title, path, matchPaths, Icon }) => {
              const active = routeActive(pathname, path, matchPaths);
              return (
                <Tooltip key={path} title={title || label} enterDelay={500}>
                  <Button
                    size="small"
                    onClick={() => navigate(path)}
                    startIcon={
                      <Icon
                        sx={{
                          fontSize: "16px !important",
                          color: active ? onInk : currentTheme.colors.text.muted,
                        }}
                      />
                    }
                    sx={{
                      flexShrink: 0,
                      minWidth: 0,
                      height: 32,
                      px: { xs: 0.85, md: 1.1 },
                      py: 0,
                      textTransform: "none",
                      fontWeight: active ? 700 : 600,
                      fontSize: "0.72rem",
                      letterSpacing: "-0.01em",
                      color: active ? onInk : currentTheme.colors.text.secondary,
                      bgcolor: active ? ink : "transparent",
                      border: `1px solid ${
                        active ? ink : currentTheme.colors.border.primary
                      }`,
                      borderRadius: currentTheme.borderRadius.md,
                      whiteSpace: "nowrap",
                      "& .MuiButton-startIcon": {
                        mr: { xs: 0, md: 0.6 },
                        ml: 0,
                      },
                      "&:hover": {
                        bgcolor: active
                          ? ink
                          : currentTheme.colors.surface.secondary,
                        color: active ? onInk : ink,
                        borderColor: active
                          ? ink
                          : currentTheme.colors.border.secondary ||
                            currentTheme.colors.border.primary,
                        "& .MuiSvgIcon-root": {
                          color: active ? onInk : ink,
                        },
                      },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{ display: { xs: "none", md: "inline" } }}
                    >
                      {label}
                    </Box>
                  </Button>
                </Tooltip>
              );
            })}
          </Box>
        </FlexBetween>

        {/* RIGHT SIDE */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            flexShrink: 0,
            flexWrap: "nowrap",
            overflow: "visible",
          }}
        >
          <NotificationBell theme={currentTheme} />
          <Tooltip title={mode === 'dark' ? 'Aydınlık Mod' : 'Karanlık Mod'}>
            <IconButton
              onClick={() => dispatch(setMode())}
              sx={iconBtnSx}
            >
              {mode === "dark" ? (
                <FontAwesomeIcon
                  icon={faSun}
                  style={{
                    fontSize: "1.05rem",
                    color: currentTheme.colors.text.secondary,
                  }}
                />
              ) : (
                <FontAwesomeIcon
                  icon={faMoon}
                  style={{
                    fontSize: "1.05rem",
                    color: currentTheme.colors.text.secondary,
                  }}
                />
              )}
            </IconButton>
          </Tooltip>

          <Box sx={{ position: "relative", flexShrink: 0, ml: 0.5 }}>
            <Button
              onClick={handleClick}
              aria-haspopup="true"
              aria-expanded={isOpen}
              sx={{
                display: "flex",
                alignItems: "center",
                textTransform: "none",
                gap: 1,
                height: 40,
                minHeight: 40,
                maxHeight: 40,
                px: 1,
                py: 0,
                flexShrink: 0,
                color: currentTheme.colors.text.primary,
                borderRadius: currentTheme.borderRadius.md,
                border: `1px solid ${isOpen ? currentTheme.colors.border.primary : "transparent"}`,
                backgroundColor: isOpen
                  ? currentTheme.colors.surface.secondary
                  : "transparent",
                "&:hover": {
                  backgroundColor: currentTheme.colors.surface.secondary,
                },
              }}
            >
              <Avatar
                src={
                  currentUser?.profileImage
                    ? `${window.location.origin}${currentUser.profileImage}`
                    : null
                }
                sx={{
                  width: 30,
                  height: 30,
                  flexShrink: 0,
                  border: `1px solid ${currentTheme.colors.border.primary}`,
                }}
              >
                <FontAwesomeIcon
                  icon={faUser}
                  style={{
                    fontSize: "0.8rem",
                    color: currentTheme.colors.text.secondary,
                  }}
                />
              </Avatar>
              <Box
                textAlign="left"
                sx={{
                  display: { xs: "none", sm: "block" },
                  minWidth: 0,
                  maxWidth: 140,
                }}
              >
                <Typography
                  fontWeight="600"
                  fontSize="0.8rem"
                  noWrap
                  sx={{ color: currentTheme.colors.text.primary, lineHeight: 1.2 }}
                >
                  {currentUser?.name || user?.name || "Kullanıcı"}
                </Typography>
                <Typography
                  fontSize="0.68rem"
                  noWrap
                  sx={{ color: currentTheme.colors.text.muted, lineHeight: 1.2 }}
                >
                  {currentUser?.occupation || user?.occupation || "—"}
                </Typography>
              </Box>
              <FontAwesomeIcon
                icon={faChevronDown}
                style={{
                  color: currentTheme.colors.text.secondary,
                  fontSize: "0.75rem",
                  transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease",
                  flexShrink: 0,
                }}
              />
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={isOpen}
              onClose={handleClose}
              disableScrollLock
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              slotProps={{
                paper: {
                  elevation: 8,
                  sx: {
                    mt: 0.75,
                    minWidth: 200,
                    background: currentTheme.colors.surface.primary,
                    backdropFilter: currentTheme.effects.backdrop,
                    border: `1px solid ${currentTheme.colors.border.primary}`,
                    borderRadius: currentTheme.borderRadius.lg,
                    boxShadow: currentTheme.shadows.glass,
                  },
                },
              }}
              MenuListProps={{ dense: true, sx: { py: 0.5 } }}
            >
              <MenuItem 
                onClick={handleProfileSettings}
                sx={{
                  color: currentTheme.colors.text.primary,
                  '&:hover': {
                    backgroundColor: currentTheme.colors.surface.secondary
                  }
                }}
              >
                <FontAwesomeIcon
                  icon={faUserGear}
                  style={{
                    marginRight: 10,
                    fontSize: "0.9rem",
                    color: currentTheme.colors.text.primary,
                  }}
                />
                Profil Ayarları
              </MenuItem>
              <MenuItem 
                onClick={handleLogout}
                sx={{
                  color: currentTheme.colors.error,
                  '&:hover': {
                    backgroundColor: currentTheme.colors.surface.secondary
                  }
                }}
              >
                <FontAwesomeIcon
                  icon={faRightFromBracket}
                  style={{
                    marginRight: 10,
                    fontSize: "0.9rem",
                    color: currentTheme.colors.error,
                  }}
                />
                Çıkış Yap
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
